import { render } from "preact";
import { useEffect, useRef } from "preact/hooks";
import { signal } from "@preact/signals";
import { startSound } from "../sound.ts";
import { Icon } from "./icon.tsx";
import { Paws } from "./paws.tsx";
import { settings } from "./settings.tsx";
import styles from "./start.module.css";

/** The start page's state: the game writes when it is ready, the page when it is gone. */
export const start = {
  /** What comes first (the title screen, or the clay left kneading) is made ready. */
  ready: signal(false),
  /** The page has slid away, for good. */
  gone: signal(false),
};

/** How long the page takes to slide away (ms). */
const LEAVE = 700;
/** Tapped rather than clicked, on touch screens. */
const VERB = matchMedia("(pointer: coarse)").matches ? "タップ" : "クリック";

/**
 * The page the game opens on, silent: the title, the paw prints while what comes first is made
 * ready, and a word that there will be sound. Once ready, a click anywhere (or Enter or Space)
 * starts the sound and slides the page off to the left, to what is behind it.
 */
export function mountStart(root: HTMLElement): void {
  render(<StartPage />, root);
}

function StartPage() {
  const page = useRef<HTMLElement>(null), leaving = useRef(false);
  const leave = async () => {
    if (!start.ready.value || leaving.current) return;
    leaving.current = true;
    // The player's touch lets the sound start (see sound.ts).
    startSound();
    // For those who would rather things kept still, it fades instead.
    const frames = settings.still.value
      ? [{ opacity: 1 }, { opacity: 0 }]
      : [{ transform: "none" }, { transform: "translateX(-100%)" }];
    await page.current!.animate(frames, {
      duration: LEAVE,
      easing: "cubic-bezier(0.65, 0, 0.35, 1)",
      fill: "forwards",
    }).finished;
    start.gone.value = true;
  };
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") void leave();
    };
    addEventListener("keydown", key);
    return () => removeEventListener("keydown", key);
  }, []);
  if (start.gone.value) return null;
  return (
    <section class={styles.start} ref={page} onClick={() => void leave()}>
      <h1 class={styles.heading}>
        <img class={styles.logo} src="logo.svg" alt="Kneading Needed -こねこのこねこね工房-" />
      </h1>
      {start.ready.value
        ? <p class={styles.prompt}>{VERB}してはじめる</p>
        : <Paws label="読み込み中" />}
      <div class={styles.notice}>
        <p>
          <Icon name="sound" />
          音が出ます。音量にご注意ください。
        </p>
        <small>効果音と BGM の音量は、設定で変えられます</small>
      </div>
    </section>
  );
}
